import type {
  BooleanResponse,
  Container,
  ContainerContainerByTypeListParams,
  ContainerContainerNamesListParams,
  ContainerListParams,
  ContainerPagedList,
  ContainerPagedListResponse,
  ContainerRequest,
  ContainerResponse,
  ContainerType,
  ContainerTypeArrayResponse,
  StringListResponse,
} from '@apiContract';

import { receivedDigitalApi } from './index';

export const containerApi = receivedDigitalApi.injectEndpoints({
  endpoints: (builder) => ({
    getContainerTypes: builder.query<ContainerType[], void>({
      query: () => 'Container/containerTypes',
      transformResponse: (response: ContainerTypeArrayResponse) => response.objectData,
    }),

    getContainerById: builder.query<Container, string>({
      query: (id) => `Container/${id}`,
      providesTags: (result) => (result?.id ? [{ type: 'Container' as const, id: result.id }] : []),
      transformResponse: (response: ContainerResponse) => response.objectData,
    }),

    getExistContainer: builder.query<Container, ContainerContainerByTypeListParams>({
      query: (params) => ({
        url: 'Container/container-by-type',
        params,
      }),
      transformResponse: (response: ContainerResponse) => response.objectData,
    }),

    getContainerNames: builder.query<string[], ContainerContainerNamesListParams>({
      query: (params) => ({
        url: 'Container/container-names',
        params,
      }),
      transformResponse: (response: StringListResponse) => response.objectData,
    }),

    getContainersFiltered: builder.query<ContainerPagedList, ContainerListParams>({
      query: (params) => ({
        url: 'Container',
        params,
      }),
      providesTags: (result) => {
        const tagsList = result?.items?.map((container) => ({ type: 'Container' as const, id: container.id })) || [];
        return ['Containers', ...tagsList];
      },
      transformResponse: (response: ContainerPagedListResponse) => response.objectData,
    }),

    createContainer: builder.mutation<Container, ContainerRequest>({
      query: (requestData) => ({
        url: 'Container',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['Containers'],
      transformResponse: (response: ContainerResponse) => response.objectData,
    }),

    updateContainer: builder.mutation<Container, ContainerRequest>({
      query: (requestData) => ({
        url: 'Container',
        method: 'PUT',
        body: requestData,
      }),
      invalidatesTags: (result) => {
        return result ? [{ type: 'Container', id: result.id }] : [];
      },
      transformResponse: (response: ContainerResponse) => response.objectData,
    }),

    deleteContainer: builder.mutation<BooleanResponse, number>({
      query: (containerId) => ({
        url: `Container/${containerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Containers'],
    }),
  }),
});

export const {
  useGetContainerTypesQuery,
  useGetContainerNamesQuery,
  useLazyGetContainerByIdQuery,
  useGetContainersFilteredQuery,
  useGetExistContainerQuery,
  useUpdateContainerMutation,
  useDeleteContainerMutation,
  useCreateContainerMutation,
} = containerApi;
